// @flow
import React from 'react';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

// This component is a local, multi-select re-implementation of the
// FilterCards molecule from @fortress-technology-solutions/fortress-component-library
// (single-select only, and we're not allowed to modify that package). It
// mirrors FilterCards' visual structure (Tooltip -> clickable area -> Card
// -> CardContent -> caller content) and borrows the array-toggle
// algorithm and aria attribute pattern from that same package's ButtonGroup
// (its `multiselect: boolean` option).
//
// This component is presentation + selection-state only: it has no
// knowledge of certifications, buckets, or dates. Consumers own computing
// `options` (id/label/count/tooltip/disabled) and interpreting `value`.

// Mirrors fortress-component-library's own hardcoded MuiCard shadow override
// so the resting shadow isn't lost when a selected card layers an inset
// accent bar onto it via `boxShadow`.
const CARD_SHADOW_BASE = '0px 2px 4px rgba(0, 0, 0, 0.0898154)';

export type PlanningRangeBreakdownItem = {
  label: string,
  count: number,
};

export type PlanningRangeOption = {
  id: string,
  label: React.Node,
  count: number,
  tooltip?: string,
  disabled?: boolean,
  // Optional per-program (or other sub-grouping) breakdown, rendered as
  // additional lines below the main count — e.g. property-level Recerts
  // cards show a per-program count breakdown, matching the pre-existing
  // static cards this component replaced. Screens that don't need this
  // (e.g. portfolio-level) simply omit it.
  breakdown?: PlanningRangeBreakdownItem[],
};

type Props = {
  options: PlanningRangeOption[],
  value: string[],
  onChange: (value: string[]) => void,
  disabled?: boolean,
};

// Mirrors the toggle algorithm used by ButtonGroup's `multiselect` mode:
// add the id if it isn't already selected, remove it if it is.
export const toggleOptionId = (value: string[], id: string): string[] => {
  const current = Array.isArray(value) ? value : [];
  return current.includes(id)
    ? current.filter((existingId) => existingId !== id)
    : [...current, id];
};

const PlanningRangeFilterOption = ({
  option,
  selected,
  isDisabled,
  onToggle,
}: {
  option: PlanningRangeOption,
  selected: boolean,
  isDisabled: boolean,
  onToggle: (id: string) => void,
}) => {
  const breakdownCount = Array.isArray(option.breakdown)
    ? option.breakdown.length
    : 0;
  const hasSingleProgram = breakdownCount === 1;
  const hasMultiplePrograms = breakdownCount >= 2;

  const button = (
    <ButtonBase
      component="button"
      type="button"
      disabled={isDisabled}
      aria-pressed={selected}
      aria-disabled={isDisabled}
      data-testid={`planning-range-filter-option-${option.id}`}
      onClick={() => {
        if (isDisabled) return;
        onToggle(option.id);
      }}
      sx={{
        display: 'block',
        textAlign: 'left',
        borderRadius: 2,
      }}
    >
      <Card
        sx={{
          minWidth: 240,
          border: '1px solid',
          borderColor: selected ? 'primary.main' : 'transparent',
          borderRadius: 2,
          transition: (theme) =>
            theme.transitions.create(['border-color', 'background-color'], {
              duration: theme.transitions.duration.short,
              easing: theme.transitions.easing.easeInOut,
            }),
          ...(!isDisabled && {
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }),
          ...(isDisabled && {
            opacity: 0.5,
          }),
        }}
      >
        <CardContent
          sx={{
            p: 2,
            '&:last-child': { pb: 2 },
            backgroundColor: selected ? 'primary.lighter' : 'common.white',
            ...(selected && {
              boxShadow: (theme) =>
                `${CARD_SHADOW_BASE}, inset 3px 0 0 ${theme.palette.primary.main}`,
            }),
          }}
        >
          <Stack sx={{ width: '100%' }}>
            <Typography
              variant="titleMedium"
              component="div"
              sx={{ fontSize: 16, fontWeight: 500, mb: 1 }}
            >
              {option.label}
            </Typography>
            <Box
              sx={{
                pb: hasMultiplePrograms ? 1.5 : 0,
                mb: hasMultiplePrograms ? 1.5 : 0,
                ...(hasMultiplePrograms && {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }),
              }}
            >
              <Typography
                variant="headlineMedium"
                component="div"
                sx={{ fontSize: 24, fontWeight: 500 }}
              >
                {option.count}
              </Typography>
            </Box>
            {hasSingleProgram && (
              <Box
                sx={{
                  mt: 1.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  width: 'fit-content',
                  minHeight: 24,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 999,
                  backgroundColor: 'common.white',
                  px: '10px',
                }}
                data-testid={`planning-range-program-pill-${option.id}`}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {option.breakdown[0].label}
                </Typography>
              </Box>
            )}
            {hasMultiplePrograms && (
              <Stack direction="column" spacing={0.75}>
                {option.breakdown.map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      ...(item.count === 0 && { opacity: 0.6 }),
                    }}
                    data-testid={`planning-range-breakdown-row-${option.id}-${item.label}`}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 13,
                        color:
                          item.count === 0 ? 'text.disabled' : 'text.secondary',
                      }}
                    >
                      {item.label}:
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 13,
                        color:
                          item.count === 0 ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {item.count}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </ButtonBase>
  );

  if (!option.tooltip) return button;

  return (
    <Tooltip title={option.tooltip} placement="top" variant="light">
      {button}
    </Tooltip>
  );
};

const PlanningRangeFilter = ({
  options = [],
  value = [],
  onChange,
  disabled = false,
}: Props) => {
  const selectedIds = Array.isArray(value) ? value : [];

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ paddingBottom: 1, overflowX: 'auto', width: '100%' }}
    >
      {(options ?? []).map((option) => {
        const isDisabled = Boolean(disabled) || Boolean(option.disabled);
        const selected = selectedIds.includes(option.id);

        return (
          <PlanningRangeFilterOption
            key={option.id}
            option={option}
            selected={selected}
            isDisabled={isDisabled}
            onToggle={(id) => onChange(toggleOptionId(selectedIds, id))}
          />
        );
      })}
    </Stack>
  );
};

export default PlanningRangeFilter;
